import { useEffect, useMemo } from "react";
import { useLocation, Navigate } from "react-router-dom";
import { LayoutDashboard, List, Building2, MapPin, Zap, Wifi, WifiOff, Activity, AlertTriangle } from "lucide-react";
import SectionLayout from "../../lib/components/SectionLayout";
import RefreshButton from "../../lib/components/RefreshButton";
import { useFleetData } from "../../lib/context/FleetDataProvider";
import useHierarchy from "../../lib/hooks/useHierarchy";
import { getCompanyDisplayName, isEnphaseUser } from "../../lib/utils/featureAccess";
import useFleetStats from "../fleet_dashboard_opensearch/hooks/useFleetStats";

const FLEET_TABS = [
  { to: "/fleet/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/fleet/list", label: "List", icon: List },
];

const FLEET_LAST_TAB_KEY = "fleet-last-tab";

/**
 * Redirect component for the /fleet index route.
 * Sends the user to whichever fleet tab they last visited.
 */
export function FleetIndexRedirect() {
  const lastTab = sessionStorage.getItem(FLEET_LAST_TAB_KEY) || "/fleet/dashboard";
  return <Navigate to={lastTab} replace />;
}

export default function FleetSection() {
  const location = useLocation();
  const isV1Route = location.pathname === "/fleet/v1" || location.pathname === "/fleet/list/v1";

  // Persist the active fleet tab so we can restore it later
  useEffect(() => {
    const path = location.pathname;
    if (path === "/fleet/list" || path === "/fleet/list/v1") {
      sessionStorage.setItem(FLEET_LAST_TAB_KEY, "/fleet/list");
    } else if (path === "/fleet/dashboard" || path === "/fleet" || path === "/fleet/v1") {
      sessionStorage.setItem(FLEET_LAST_TAB_KEY, "/fleet/dashboard");
    }
  }, [location.pathname]);

  const { state: fleetState, refresh: refreshFleet, requestFetch } = useFleetData();
  const enphaseUser = isEnphaseUser();
  const { stations: hierarchyStations } = useHierarchy({ enabled: !enphaseUser });

  // Only fetch ev_charger data when on V1 routes (V2 uses stats API instead)
  useEffect(() => {
    if (isV1Route) requestFetch();
  }, [isV1Route, requestFetch]);

  // ── Stats API for V2 banner ──
  const { data: statsData, isLoading: statsLoading, refresh: refreshStats } = useFleetStats();

  const hierarchyStats = useMemo(() => {
    const incoming = Array.isArray(fleetState?.data) ? fleetState.data : [];
    const uniqueSites = new Set();

    incoming.forEach(charger => {
      if (charger.siteId) uniqueSites.add(charger.siteId);
    });

    const stationCount = hierarchyStations.filter(
      station => (station.stationName || station.name || "").toLowerCase() !== "unassigned"
    ).length;

    return {
      stations: stationCount,
      totalSites: uniqueSites.size,
    };
  }, [fleetState?.data, hierarchyStations]);

  const companyName = getCompanyDisplayName();

  // ── V1 banner KPIs (from fleet chargers list) ──
  const v1Kpis = useMemo(() => {
    const data = Array.isArray(fleetState?.data) ? fleetState.data : [];
    if (!data.length) return null;
    const total = data.length;
    const online = data.filter(d => d.isConnected).length;
    const offline = total - online;
    const charging = data.filter(d => d.connectors?.some(c => c.connectorStatusType === "CHARGING")).length;
    const faulted = data.filter(d => d.isFaulted).length;
    return { total, online, offline, charging, faulted };
  }, [fleetState?.data]);

  // ── V2 banner KPIs (from stats API) ──
  const v2Kpis = useMemo(() => {
    if (!statsData) return null;
    return {
      total: Number(statsData.totalChargers) || 0,
      online: Number(statsData.onlineCount) || 0,
      offline: Number(statsData.offlineCount) || 0,
      charging: Number(statsData.connectorStatusTypeCounts?.CHARGING) || 0,
      faulted: Number(statsData.connectorStatusTypeCounts?.FAULTED) || 0,
      sites: Number(statsData.totalSites) || 0,
    };
  }, [statsData]);

  // ── Build hero stats JSX for V1 ──
  const v1HeroStats = v1Kpis ? (
    <>
      {!enphaseUser && (
        <div className="hero-stat hero-stat--group">
          <span className="hero-stat__group-label">Stations</span>
          <div className="hero-stat__group-items">
            <div className="hero-stat__group-item">
              <Building2 size={12} className="hero-stat__icon hero-stat__icon--teal" />
              <span className="hero-stat__value">{hierarchyStats.stations}</span>
            </div>
          </div>
        </div>
      )}
      <div className="hero-stat hero-stat--group">
        <span className="hero-stat__group-label">Sites</span>
        <div className="hero-stat__group-items">
          <div className="hero-stat__group-item">
            <MapPin size={12} className="hero-stat__icon hero-stat__icon--purple" />
            <span className="hero-stat__value">{hierarchyStats.totalSites}</span>
          </div>
        </div>
      </div>
      <div className="hero-stat hero-stat--group">
        <span className="hero-stat__group-label">Chargers</span>
        <div className="hero-stat__group-items">
          <div className="hero-stat__group-item">
            <Zap size={12} className="hero-stat__icon hero-stat__icon--teal" />
            <span className="hero-stat__value">{v1Kpis.total}</span>
            <span className="hero-stat__label">Total</span>
          </div>
          <div className="hero-stat__group-item">
            <Wifi size={12} className="hero-stat__icon hero-stat__icon--green" />
            <span className="hero-stat__value">{v1Kpis.online}</span>
            <span className="hero-stat__label">Online</span>
          </div>
          <div className="hero-stat__group-item">
            <WifiOff size={12} className="hero-stat__icon hero-stat__icon--red" />
            <span className="hero-stat__value">{v1Kpis.offline}</span>
            <span className="hero-stat__label">Offline</span>
          </div>
          <div className="hero-stat__group-item">
            <Activity size={12} className="hero-stat__icon hero-stat__icon--blue" />
            <span className="hero-stat__value">{v1Kpis.charging}</span>
            <span className="hero-stat__label">Charging</span>
          </div>
          <div className="hero-stat__group-item">
            <AlertTriangle size={12} className="hero-stat__icon hero-stat__icon--amber" />
            <span className="hero-stat__value">{v1Kpis.faulted}</span>
            <span className="hero-stat__label">Faulted</span>
          </div>
        </div>
      </div>
    </>
  ) : null;

  // ── Build hero stats JSX for V2 (OpenSearch) ──
  const v2HeroStats = v2Kpis ? (
    <>
      <div className="hero-stat hero-stat--group">
        <span className="hero-stat__group-label">Sites</span>
        <div className="hero-stat__group-items">
          <div className="hero-stat__group-item">
            <MapPin size={12} className="hero-stat__icon hero-stat__icon--purple" />
            <span className="hero-stat__value">{v2Kpis.sites}</span>
          </div>
        </div>
      </div>
      <div className="hero-stat hero-stat--group">
        <span className="hero-stat__group-label">Chargers</span>
        <div className="hero-stat__group-items">
          <div className="hero-stat__group-item">
            <Zap size={12} className="hero-stat__icon hero-stat__icon--teal" />
            <span className="hero-stat__value">{v2Kpis.total.toLocaleString()}</span>
            <span className="hero-stat__label">Total</span>
          </div>
          <div className="hero-stat__group-item">
            <Wifi size={12} className="hero-stat__icon hero-stat__icon--green" />
            <span className="hero-stat__value">{v2Kpis.online.toLocaleString()}</span>
            <span className="hero-stat__label">Online</span>
          </div>
          <div className="hero-stat__group-item">
            <WifiOff size={12} className="hero-stat__icon hero-stat__icon--red" />
            <span className="hero-stat__value">{v2Kpis.offline.toLocaleString()}</span>
            <span className="hero-stat__label">Offline</span>
          </div>
          <div className="hero-stat__group-item">
            <Activity size={12} className="hero-stat__icon hero-stat__icon--blue" />
            <span className="hero-stat__value">{v2Kpis.charging.toLocaleString()}</span>
            <span className="hero-stat__label">Charging</span>
          </div>
          <div className="hero-stat__group-item">
            <AlertTriangle size={12} className="hero-stat__icon hero-stat__icon--amber" />
            <span className="hero-stat__value">{v2Kpis.faulted.toLocaleString()}</span>
            <span className="hero-stat__label">Faulted</span>
          </div>
        </div>
      </div>
    </>
  ) : null;

  // ── Pick banner based on active route ──
  const heroStats = isV1Route ? v1HeroStats : v2HeroStats;
  const handleRefresh = isV1Route ? refreshFleet : refreshStats;
  const isRefreshing = isV1Route ? fleetState?.isLoading : statsLoading;

  return (
    <SectionLayout
      companyName={companyName}
      title="Fleet Management"
      subtitle="Monitor and manage your entire EV charger fleet"
      tabs={FLEET_TABS}
      tabActions={<RefreshButton onClick={handleRefresh} tooltip="Refresh fleet data" disabled={isRefreshing} />}
      heroStats={heroStats}
    />
  );
}
